import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { FileCheck2, CheckCircle2, Undo2, Eye, ChevronDown, History } from "lucide-react";

const TONE = { draft: "outline", submitted: "warning", under_review: "warning", accepted: "success", returned: "destructive" };
const REVIEWER = "ghg.lead@tropigarden.demo";

export default function SupplierDisclosures() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);

  const load = () => api.get("/supply-chain/disclosures").then((r) => setData(r.data)).catch(() => setData({ disclosures: [], funnel: {} }));
  useEffect(() => { load(); }, []);

  const review = async (did, action) => {
    try {
      await api.post(`/supply-chain/disclosures/${did}/review`, { action, reviewer: REVIEWER, note: `${action} via GHG review` });
      toast.success(`Disclosure ${action === "accept" ? "accepted" : action === "return" ? "returned" : "under review"}`);
      load();
    } catch (e) { toast.error("Review failed"); }
  };

  if (!data) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading disclosures…</div>;

  return (
    <div className="p-6 space-y-5" data-testid="disclosures-page">
      <div>
        <div className="label-eyebrow mb-1">Supply Chain</div>
        <h2 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2"><FileCheck2 className="h-6 w-6 text-accent" /> Disclosure Lifecycle</h2>
        <p className="text-muted-foreground text-sm mt-1 max-w-2xl">Supplier submissions move through a versioned lifecycle — draft → submitted → under review → accepted / returned. Every revision is retained; suppliers reuse prior answers across cycles.</p>
        <p className="text-[11px] text-muted-foreground mt-1 max-w-2xl" data-testid="disclosures-scope-note">
          These count formal ESG disclosures raised for GHG-lead review — a separate stage from a supplier simply completing their portal profile (tracked as "Profile submitted" in Supplier Portal Admin). When a supplier completes their portal profile, a Draft disclosure is auto-created here for your GHG lead to review and formally submit.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {Object.entries(data.funnel).map(([k, v]) => (
          <div key={k} className="card-flat p-4" data-testid={`funnel-${k}`}>
            <div className="label-eyebrow text-[10px] mb-1 capitalize">{k.replace(/_/g, " ")}</div>
            <div className="font-display text-3xl font-bold">{v}</div>
          </div>
        ))}
      </div>

      <div className="space-y-3">
        {data.disclosures.map((d) => (
          <div key={d.id} className="card-flat p-4" data-testid={`disclosure-${d.id}`}>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <div className="font-display font-bold">{d.supplier_name}</div>
                <div className="text-[10px] font-mono text-muted-foreground">FY{d.period} · v{d.current_version} · {d.versions?.length || 0} revision(s){d.reusable ? " · reusable" : ""}</div>
              </div>
              <Badge variant={TONE[d.status]} className="text-[8px] uppercase">{d.status.replace(/_/g, " ")}</Badge>
              {d.status !== "accepted" && (
                <div className="flex gap-1.5">
                  {d.status !== "under_review" && <Button size="sm" variant="outline" onClick={() => review(d.id, "under_review")} data-testid={`disc-review-${d.id}`}><Eye className="h-3.5 w-3.5" /> Review</Button>}
                  <Button size="sm" variant="primary" onClick={() => review(d.id, "accept")} data-testid={`disc-accept-${d.id}`}><CheckCircle2 className="h-3.5 w-3.5" /> Accept</Button>
                  <Button size="sm" variant="ghost" onClick={() => review(d.id, "return")} data-testid={`disc-return-${d.id}`}><Undo2 className="h-3.5 w-3.5" /> Return</Button>
                </div>
              )}
              <button onClick={() => setOpen(open === d.id ? null : d.id)} data-testid={`disc-history-${d.id}`}>
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open === d.id ? "rotate-180" : ""}`} />
              </button>
            </div>
            {open === d.id && (
              <div className="mt-3 pt-3 border-t border-border space-y-2">
                <div className="label-eyebrow text-[10px] flex items-center gap-1"><History className="h-3 w-3" /> Version history</div>
                {[...(d.versions || [])].reverse().map((v) => (
                  <div key={v.version} className="p-2.5 rounded-sm border border-border bg-surface" data-testid={`disc-${d.id}-v${v.version}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs font-bold">v{v.version}</span>
                      <Badge variant={TONE[v.status]} className="text-[7px] uppercase">{v.status.replace(/_/g, " ")}</Badge>
                      <span className="text-[10px] text-muted-foreground">by {v.submitted_by}</span>
                      {v.reused_from_version && <Badge variant="outline" className="text-[7px] uppercase">reused v{v.reused_from_version}</Badge>}
                    </div>
                    <div className="text-[10px] font-mono text-muted-foreground mt-1">{Object.entries(v.payload || {}).map(([k, val]) => `${k}: ${val}`).join(" · ")}</div>
                    {v.reviewer_note && <div className="text-[10px] text-accent mt-1">Reviewer: {v.reviewer_note}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        {!data.disclosures.length && <div className="card-flat p-8 text-center text-muted-foreground text-sm">No disclosures submitted yet.</div>}
      </div>
    </div>
  );
}
